// Types adapted from the backend FastAPI models
export interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
  turn_correlation_id?: string;
  grade?: GradeData; // Associated grading data for the message
  toolContent?: ToolCallContent[]; // Tool calls made for this message
  thinkingContent?: string; // Reasoning/thinking tokens from model reasoning blocks
}

export interface ChatRequest {
  message: string;
  user_context: {
    user_id?: string;
    user_login?: string;
    user_type?: string;
    source_name?: string;
    source_type?: string;
    is_training_environment?: boolean;
  };
  thread_id?: string;
  session_id: string;
  turn_correlation_id?: string;
  view_context?: {
    visible_data?: Record<string, unknown>;
  };
}

export interface ChatResponse {
  response: string;
  agent_used: boolean;
  thread_id: string;
}

export interface ApiUser {
  user_id: string | null;
  email: string | null;
  name: string | null;
  roles: string[];
}

// Autocomplete types
export interface AutocompleteRequest {
  search_term: string;
  limit?: number;
}

export interface AutocompleteResponse {
  domain: string;
  display_name: string;
  records: string[];
  count: number;
  search_term: string;
  performance: {
    duration_ms: number;
    results_count: number;
  };
}

// Document management types
export interface DocumentListRequest {
  authorization_id: string;
  ui_mode?: string;  // Domain mode (authorization, complaint, etc.)
}

export interface DocumentInfo {
  document_guid: string;
  document_name: string;
  document_type?: string;
  document_description?: string;
  document_created_on?: string;
  processing_stage?: string;
  file_extension: string;
}

export interface DocumentListResponse {
  authorization_id: string;
  documents: DocumentInfo[];
  count: number;
}

export interface DocumentDownloadRequest {
  authorization_id: string;
  document_guid: string;
  document_name: string;
  ui_mode?: string;
}

// Tool content types
export interface ToolChunk {
  id: string;
  content: string;
  metadata: Record<string, any>;
}

export interface ToolCallContent {
  tool_name: string;
  display_name: string;
  chunks: ToolChunk[];
}

// Todo item from the agent's write_todos tool
export interface TodoItem {
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
}

// Streaming response types
export interface StreamMessage {
  content?: unknown;
  type: 'message' | 'error' | 'warning' | 'status' | 'stream_end' | 'message_end' | 'thread_id' | 'turn_metadata' | 'grade' | 'tool_content' | 'thinking' | 'todos';
  turn_id?: string;
  is_graded?: boolean;
}

// Health check response
export interface HealthResponse {
  status: string;
  timestamp: string;
  components?: Record<string, unknown> | null;
}

// Thread management types
export interface UserThread {
  Id: number;
  UserId: string;
  ThreadId: string;
  AuthorizationId?: string;
  InitialQuestion?: string;
  Domain?:string;
  UserEmail?: string;
  CreatedAt: string;
  IsActive: boolean;
  LastUsedAt?: string;
  RowCreatedTs: string;
  RowModifiedTs: string;
}

export interface ThreadListResponse {
  threads: UserThread[];
  count: number;
}

export interface ThreadMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string | null;
}

export interface ThreadMessagesResponse {
  thread_id: string;
  messages: ThreadMessage[];
  count: number;
}

// Grading types
export interface GradeData {
  user_question: string;
  score: number;
  reasoning: string;
  timestamp: number;
  evaluator?: string;
  turn_correlation_id?: string;
  conversation_turn?: number | null;
  metric_name?: string;
}

export interface GradingQueryRequest {
  user_context: ChatRequest['user_context'];
  thread_id: string;
  session_id?: string;
  turn_correlation_id?: string;
  conversation_turn?: number;
}

export interface GradingQueryResponse {
  thread_id: string;
  grades: Array<{
    question: string;
    score: number;
    rationale: string;
    created_at: string | null;
    evaluator: string;
    conversation_turn: number | null;
    turn_correlation_id: string;
    metric_name: string;
    score_scale: { minimum: number; maximum: number };
  }>;
  count: number;
}

// Adhoc processing types
export interface AdhocProcessRequest {
  authorization_id: string;
}

export interface AdhocProcessResponse {
  success: boolean;
  message: string;
  authorization_id: string;
  queue_name: string;
}

export interface ProcessingStatusRecord {
  complaintId: string;
  status: string;
  source: string;
  metadataRetrievalDate: string | null;
  documentDownloadDate: string | null;
  ocrCompletionDate: string | null;
  ready: boolean;
  lastUpdatedDate: string | null;
  documentCount: number | null;
  documentsDownloaded: number | null;
  documentsOCRProcessed: number | null;
  errorMessage: string | null;
  uploadedAt: string | null;
}

export interface ProcessingStatusResponse {
  records: ProcessingStatusRecord[];
  count: number;
}

// Agent-related types for medical management
export interface AgentConfig {
  name: string;
  description: string;
  systemPrompt?: string;
}

// UI State types
export interface ChatState {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  authNumber?: string;
  threadId?: string;
  sessionId?: string; // Frontend-generated session ID for request isolation
  aiServicesAvailable: boolean;
  isStreaming: boolean;
}

// Theme types
export interface AppTheme {
  name: string;
  isDark: boolean;
}

export const DEFAULT_THEMES: AppTheme[] = [
  { name: 'Light', isDark: false },
  { name: 'Dark', isDark: true }
];
